/**
 * UATFeedbackModal.jsx — Structured UAT feedback capture form.
 *
 * Two input modes:
 *   TYPE   — standard text fields
 *   SPEAK  — audio recorder → native transcript + English interpretation review
 *
 * Context is auto-populated from the current route, user role, tenant and
 * institution. Testers only need to describe the problem; they do not need
 * to understand system internals.
 *
 * Design invariants (per owner spec):
 *   - Original transcript is preserved as the authoritative record.
 *   - Normalized English text is a working copy, clearly labelled as such.
 *   - Audio is NOT retained unless explicitly consented (Phase 1: no audio retention).
 *   - Machine transcript shown to tester for review/edit before submission.
 *   - Provider NOT_CONFIGURED → voice tab visible but disabled with explanation.
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  MessageSquare,
  Mic,
  AlertTriangle,
  ChevronDown,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { UATAudioRecorder } from "./UATAudioRecorder";

const API = process.env.REACT_APP_BACKEND_URL;

const FEEDBACK_TYPES = [
  { value: "BUG", label: "Bug" },
  { value: "USABILITY", label: "Usability" },
  { value: "DATA_ISSUE", label: "Data Issue" },
  { value: "CONFIGURATION", label: "Configuration" },
  { value: "PERFORMANCE", label: "Performance" },
  { value: "ACCESS_SECURITY", label: "Access / Security" },
  { value: "INTEGRATION", label: "Integration" },
  { value: "ENHANCEMENT", label: "Enhancement" },
  { value: "DOCUMENTATION", label: "Documentation" },
];

const SEVERITY_OPTIONS = [
  { value: "CRITICAL", label: "Critical", color: "text-red-600" },
  { value: "HIGH", label: "High", color: "text-orange-600" },
  { value: "MEDIUM", label: "Medium", color: "text-amber-600" },
  { value: "LOW", label: "Low", color: "text-blue-600" },
  { value: "INFORMATIONAL", label: "Informational", color: "text-muted-foreground" },
];

function detectModuleFromRoute(pathname) {
  if (pathname.startsWith("/setup")) return "Setup Studio";
  if (pathname.startsWith("/evaluation")) return "Evaluation";
  if (pathname.startsWith("/assessment")) return "Assessment";
  if (pathname.startsWith("/gradebook")) return "Gradebook";
  if (pathname.startsWith("/admissions")) return "Admissions";
  if (pathname.startsWith("/finance")) return "Finance";
  if (pathname.startsWith("/students")) return "Student Records";
  if (pathname.startsWith("/attendance")) return "Attendance";
  return "General";
}

export function UATFeedbackModal({ onClose, uatConfig, user, token }) {
  const [mode, setMode] = useState("TYPE"); // TYPE | SPEAK
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Form state
  const [feedbackType, setFeedbackType] = useState("BUG");
  const [severity, setSeverity] = useState("MEDIUM");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState("");
  const [expected, setExpected] = useState("");
  const [actual, setActual] = useState("");

  // Voice/multilingual state (populated by UATAudioRecorder)
  const [sttResult, setSttResult] = useState(null);

  // Auto-populated context
  const context = {
    current_route: window.location.pathname,
    module: detectModuleFromRoute(window.location.pathname),
    screen: window.location.pathname,
    build_version: process.env.REACT_APP_VERSION || "dev",
    environment: "UAT",
    browser: navigator.userAgent.split(" ").pop(),
    os: navigator.platform,
  };

  const voiceAvailable =
    uatConfig?.voice_enabled && uatConfig?.voice_provider_status === "AVAILABLE";

  const handleSttReady = useCallback((result) => {
    setSttResult(result);
    // Pre-fill title from English interpretation if title is empty
    if (!title && result.normalized_english_text) {
      const firstLine = result.normalized_english_text.split(".")[0].trim();
      if (firstLine && firstLine.length <= 200) setTitle(firstLine);
    }
    // Pre-fill description from native transcript
    if (!description && result.original_text) {
      setDescription(result.original_text);
    }
  }, [title, description]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;

    setSubmitting(true);
    setSubmitError(null);

    const payload = {
      environment: context.environment,
      build_version: context.build_version,
      module: context.module,
      screen: context.screen,
      current_route: context.current_route,
      feedback_type: feedbackType,
      severity,
      title: title.trim(),
      description: description.trim() || null,
      steps_to_reproduce: steps.trim() || null,
      expected_behavior: expected.trim() || null,
      actual_behavior: actual.trim() || null,
      browser: context.browser,
      os: context.os,
      // Multilingual fields (only populated if voice mode was used)
      ...(sttResult && {
        original_text: sttResult.original_text,
        original_language: sttResult.original_language,
        normalized_english_text: sttResult.normalized_english_text,
        translation_provider: sttResult.translation_provider,
        translation_model: sttResult.translation_model,
        audio_consented: false, // Phase 1: no audio retention
      }),
    };

    try {
      const res = await fetch(`${API}/api/uat/feedback`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail?.message || err.detail || "Submission failed.");
      }

      setSubmitSuccess(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div
        className="flex flex-col items-center justify-center py-12 text-center"
        data-testid="uat-submit-success"
      >
        <CheckCircle2 className="h-10 w-10 text-emerald-500 mb-4" />
        <h3 className="text-base font-semibold text-foreground mb-1">Feedback submitted</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Thank you. Your feedback has been recorded and will be reviewed by the team.
        </p>
        <button
          onClick={onClose}
          className="text-xs font-medium px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-colors"
          data-testid="uat-close-after-submit"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="uat-feedback-form">
      {/* ── Mode toggle ── */}
      <div className="flex items-center rounded-lg border border-border bg-muted/40 p-0.5 gap-0.5">
        <button
          type="button"
          onClick={() => setMode("TYPE")}
          data-testid="uat-mode-type"
          className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded-md transition-colors ${
            mode === "TYPE"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <MessageSquare className="h-3.5 w-3.5" />
          Type Feedback
        </button>
        <button
          type="button"
          onClick={() => voiceAvailable && setMode("SPEAK")}
          data-testid="uat-mode-speak"
          disabled={!voiceAvailable}
          title={!voiceAvailable ? "Voice input not available — contact your administrator" : undefined}
          className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded-md transition-colors ${
            mode === "SPEAK"
              ? "bg-background text-foreground shadow-sm"
              : voiceAvailable
              ? "text-muted-foreground hover:text-foreground"
              : "text-muted-foreground/50 cursor-not-allowed"
          }`}
        >
          <Mic className="h-3.5 w-3.5" />
          Speak Feedback
          {!voiceAvailable && (
            <span className="text-[9px] uppercase tracking-wider border border-border rounded px-1 py-0.5 ml-1">
              {uatConfig?.voice_provider_status === "NOT_CONFIGURED" ? "Not set up" : "Unavailable"}
            </span>
          )}
        </button>
      </div>

      {/* ── Auto-populated context (read-only) ── */}
      <div
        className="flex items-center gap-3 px-3 py-2 rounded-lg border border-border bg-muted/30 text-[10px] text-muted-foreground flex-wrap"
        data-testid="uat-context-strip"
      >
        <span className="font-medium text-foreground">{context.module}</span>
        <span>·</span>
        <span className="font-mono truncate max-w-[180px]">{context.current_route}</span>
        {user?.role && (
          <>
            <span>·</span>
            <span>{user.role}</span>
          </>
        )}
        {context.build_version !== "dev" && (
          <>
            <span>·</span>
            <span>v{context.build_version}</span>
          </>
        )}
      </div>

      {/* ── Voice mode ── */}
      {mode === "SPEAK" && (
        <div
          className="p-4 rounded-lg border border-border bg-muted/10"
          data-testid="uat-speak-panel"
        >
          <UATAudioRecorder
            onTranscriptReady={handleSttReady}
            token={token}
          />
        </div>
      )}

      {/* ── Type + title (always visible) ── */}
      <div>
        <label className="block text-xs font-semibold text-foreground mb-1.5">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Short description of the issue"
          required
          maxLength={500}
          className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary"
          data-testid="uat-title-input"
        />
      </div>

      {/* ── Type + severity row ── */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-foreground mb-1.5">Type</label>
          <select
            value={feedbackType}
            onChange={(e) => setFeedbackType(e.target.value)}
            className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid="uat-type-select"
          >
            {FEEDBACK_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-foreground mb-1.5">Severity</label>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid="uat-severity-select"
          >
            {SEVERITY_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Description ── */}
      <div>
        <label className="block text-xs font-semibold text-foreground mb-1.5">
          What were you trying to do?
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="Describe the context and what you were trying to accomplish"
          className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground/60 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
          data-testid="uat-description-input"
        />
      </div>

      {/* ── Expected / Actual ── */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-foreground mb-1.5">
            What did you expect?
          </label>
          <textarea
            value={expected}
            onChange={(e) => setExpected(e.target.value)}
            rows={2}
            className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground/60 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid="uat-expected-input"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-foreground mb-1.5">
            What happened instead?
          </label>
          <textarea
            value={actual}
            onChange={(e) => setActual(e.target.value)}
            rows={2}
            className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground/60 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid="uat-actual-input"
          />
        </div>
      </div>

      {/* ── Steps to reproduce (collapsible) ── */}
      <div>
        <label className="block text-xs font-semibold text-foreground mb-1.5">
          Steps to reproduce{" "}
          <span className="font-normal text-muted-foreground">(optional)</span>
        </label>
        <textarea
          value={steps}
          onChange={(e) => setSteps(e.target.value)}
          rows={2}
          placeholder="1. Go to...  2. Click...  3. See..."
          className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground/60 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
          data-testid="uat-steps-input"
        />
      </div>

      {/* ── Submit error ── */}
      {submitError && (
        <div className="flex items-start gap-2 p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30">
          <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
          <p className="text-xs text-red-700 dark:text-red-300">{submitError}</p>
        </div>
      )}

      {/* ── Submit button ── */}
      <div className="flex items-center justify-end gap-3 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="text-xs font-medium px-3 py-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
          data-testid="uat-cancel-btn"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting || !title.trim()}
          className="flex items-center gap-1.5 text-xs font-medium px-4 py-1.5 rounded-lg bg-primary text-primary-foreground border border-primary hover:opacity-90 transition-colors disabled:opacity-60"
          data-testid="uat-submit-btn"
        >
          {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {submitting ? "Submitting…" : "Submit Feedback"}
        </button>
      </div>
    </form>
  );
}
